import { faCamera } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormApi } from 'lib-node-amb-renderer';
import { useState } from 'react';

import { useActiveFieldStore } from '~/common/componentMapper/QuestionExtraction/activeFieldStore';
import Button from '~/common/components/Button';
import Stack from '~/common/components/Stacks/Stack';
import { SNAPSHOT_BUTTON_LABEL } from '~/common/constants';

import { StyledContainerBox } from '../QuestionBox/Styled';
import { TITLE_ICON_TYPE } from '../QuestionBox/constants';
import ExtractionDataIcon from './components/ExtractionDataIcon';
import Footer from './components/Footer';
import { inputComponentNames } from './constants';
import { handleTitleConfidenceLevelColor } from './handlers';

type QuestionExtractionProps = {
  sx?: any;
  buttonMargin?: string;
  children?: React.ReactNode;
  fields?: any[];
  id?: string;
  iconType?: string | number;
  questionId?: string;
  aiResponse?: {
    answer?: string;
    detailedAnswer?: string;
    confidence?: number;
    confidenceLevel?: number | string;
    snippets?: any[];
  };
  showBorderBottom?: boolean;
  showBorderTop?: boolean;
  showSnapShotButton?: boolean;
  showGaugeIcon?: boolean;
};

export default function QuestionExtraction({
  id = '',
  sx,
  buttonMargin,
  children,
  questionId,
  fields = [],
  iconType: stringifiedIconType,
  aiResponse = null,
  showBorderBottom = false,
  showBorderTop = true,
  showSnapShotButton = false,
  showGaugeIcon = true,
}: Readonly<QuestionExtractionProps>) {
  const { activeField } = useActiveFieldStore();
  const { renderForm } = useFormApi();
  const [showFooter, setShowFooter] = useState(false);
  const detailedAnswer = aiResponse?.detailedAnswer ?? '';
  const answer = aiResponse?.answer ?? '';
  const confidenceLevel =
    typeof aiResponse?.confidenceLevel === 'number'
      ? aiResponse?.confidenceLevel
      : Number.parseInt(aiResponse?.confidenceLevel);
  const iconType = Number.parseInt(String(stringifiedIconType));
  const snippets = aiResponse?.snippets ?? [];

  const questionField = fields.filter((field) => {
    if (field?.name && field?.component) {
      return (
        inputComponentNames.includes(field.component) &&
        field.name === questionId
      );
    }
  });

  const labelInputProps = questionField[0]?.labelInputProps;

  const isConfidenceVisible =
    !!detailedAnswer &&
    !!iconType &&
    (iconType === TITLE_ICON_TYPE.BOLT ||
      iconType === TITLE_ICON_TYPE.AUTOMATION);

  const isBOLTAnswerVisible =
    !!iconType &&
    (iconType === TITLE_ICON_TYPE.NEW || iconType === TITLE_ICON_TYPE.USER);

  const isIconVisible = !!iconType && iconType !== 0;
  const isActive =
    activeField === questionId ||
    (!!labelInputProps && labelInputProps?.name === activeField);

  const hasSnippets = !!snippets.length;

  const handlePDFOnFocus = () => {
    window.postMessage({ type: 'pdfCitation', payload: snippets });
  };

  return (
    <StyledContainerBox
      id={id}
      {...sx}
      isActive={isActive}
      showBorderTop={showBorderTop}
      showBorderBottom={showBorderBottom}
    >
      <Stack direction="column" sx={{ width: '100%' }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {/* INFO: only displays inputs that match questionId */}
          {(questionField.length > 0 || children) && (
            <Stack
              direction="row"
              sx={{ justifyContent: 'flex-start', flex: 1, minWidth: 0 }}
            >
              {children ?? renderForm(questionField)}
            </Stack>
          )}
          {isIconVisible && <ExtractionDataIcon iconType={iconType} />}
        </Stack>
        <Footer
          answer={answer}
          confidenceLevel={confidenceLevel}
          detailedAnswer={detailedAnswer}
          showFooter={showFooter}
          setShowFooter={setShowFooter}
          isConfidenceVisible={isConfidenceVisible}
          isBOLTAnswerVisible={isBOLTAnswerVisible}
          handleConfidenceLevelColor={handleTitleConfidenceLevelColor}
          showGaugeIcon={showGaugeIcon}
        />
        {showSnapShotButton && hasSnippets && (
          <Button
            padding={sx?.padding ?? '1.5rem'}
            margin={buttonMargin ?? '1rem 0.5rem 0.5rem 0'}
            paddingLeft={sx?.paddingLeft ?? '1rem'}
            paddingRight={sx?.paddingRight ?? '1rem'}
            width={sx?.width ?? '2rem'}
            height={sx?.height ?? '2rem'}
            minWidth={sx?.minWidth ?? '2.5rem'}
            minHeight={sx?.minHeight ?? '2.5rem'}
            borderRadius={sx?.borderRadius ?? '0.375rem'}
            isPrimary={false}
            onClick={() => handlePDFOnFocus()}
            aria-label={SNAPSHOT_BUTTON_LABEL}
          >
            <FontAwesomeIcon icon={faCamera} />
          </Button>
        )}
      </Stack>
    </StyledContainerBox>
  );
}
